import React, { useState, useEffect } from 'react';
import DataTable from 'react-data-table-component';
import { Box, Typography, FormControl, Select, MenuItem, TextField, FormControlLabel, CircularProgress } from '@mui/material';
import { Edit, Delete } from '@mui/icons-material';  
import { Switch, styled } from '@mui/material';
import axios from 'axios';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useNavigate } from 'react-router-dom';

const IOSSwitch = styled((props) => (
  <Switch focusVisibleClassName=".Mui-focusVisible" disableRipple {...props} />
))(({ theme }) => ({
  width: 42,
  height: 26,
  padding: 0,
  '& .MuiSwitch-switchBase': {
    padding: 0,
    margin: 2,
    transitionDuration: '300ms',
    '&.Mui-checked': {
      transform: 'translateX(16px)',
      color: '#fff',
      '& + .MuiSwitch-track': {
        backgroundColor: 'green',
        opacity: 1,
        border: 0,
      },
    },
  },
  '& .MuiSwitch-thumb': {
    boxSizing: 'border-box',
    width: 22,
    height: 22,
  },
  '& .MuiSwitch-track': {
    borderRadius: 26 / 2,
    backgroundColor: 'red',
    opacity: 1,
  },
}));

const MyTable = () => {
  const [data, setData] = useState([]);  
  const [entriesPerPage, setEntriesPerPage] = useState(10); 
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(1);
  const [selectedRows, setSelectedRows] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [isLoading, setIsLoading] = useState(true);  // New loading state

  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedRows([]);
    } else {
      const allIds = data.map(item => item.id);
      setSelectedRows(allIds);
    }
    setSelectAll(!selectAll);
  };

  const handleRowCheckboxChange = (rowId) => {
    if (selectedRows.includes(rowId)) {
      setSelectedRows(selectedRows.filter(id => id !== rowId));
    } else {
      setSelectedRows([...selectedRows, rowId]);
    }
  };

  useEffect(() => {
    const fetchCategory = async () => {
      try {
        setIsLoading(true);  // Set loading to true when fetching data
        const response = await axios.get(`${process.env.REACT_APP_API_URL}/categories/allcategories`);
        const apiData = response.data;
        const formattedData = apiData?.categories?.map(item => ({
          id: item._id,              
          image: item.image,         
          itemName: item.name,       
          quantity: item.itemCount,  
          publish: item.publish      
        }));
        setData(formattedData);
        setIsLoading(false);  // Set loading to false after data is fetched
      } catch (error) {
        setIsLoading(false);  // Set loading to false if error occurs
        console.error("Error fetching data:", error);
      }
    };
  
    fetchCategory();
  }, []);

  const deleteCategory = async (itemId) => {
    try {
      const response = await axios.delete(`${process.env.REACT_APP_API_URL}/categories/delete/${itemId}`);
      console.log(response.data)
      const categoryname = response.data?.category?.name;
      setData(prevData => prevData.filter(item => item.id !== itemId));
      toast.success(`Category has been deleted.`);
    } catch (error) {
      console.error("Error deleting category:", error);
      toast.error(`Failed to delete category with ID: ${itemId}`);
    }
  };

    const handlePublishToggle = async (row, updatedPublishStatus) => {
      // Optimistically update the UI immediately
      const updatedData = data.map(item =>
        item.id === row.id ? { ...item, publish: updatedPublishStatus } : item
      );
      setData(updatedData);
    
      try {
        // Send the API request to update the publish status
        const response = await axios.put(
          `${process.env.REACT_APP_API_URL}/categories/edit/${row.id}`,
          { publish: updatedPublishStatus }
        );
    
        // Refetch the data after the update to ensure it's in sync
        const fetchCategory = async () => {
          try {
            const response = await axios.get(`${process.env.REACT_APP_API_URL}/categories/allcategories`);
            const apiData = response.data;
            const formattedData = apiData?.categories?.map(item => ({
              id: item._id,
              image: item.image,
              itemName: item.name,
              quantity: item.itemCount,
              publish: item.publish, // Ensure publish status is fetched correctly
            }));
            setData(formattedData);
          } catch (error) {
            console.error("Error fetching data after publish update:", error);
          }
        };
    
        fetchCategory();  // Refresh data after publishing
    
        toast.success('Publish status updated successfully');
      } catch (error) {
        console.error('Error updating publish status:', error);
    
        // Revert the optimistic UI update if API call fails
        const revertedData = data.map(item =>
          item.id === row.id ? { ...item, publish: !updatedPublishStatus } : item
        );
        setData(revertedData);
    
        toast.error('Failed to update publish status');
      }
    };

  
  
  const navigate = useNavigate();
  const columns = [
    {
      name: (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }} checked={selectAll} onChange={handleSelectAll}>
          <Box>
            <input type="checkbox" />
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', color: 'green' }}>
            <Delete sx={{ cursor: 'pointer', padding: '0px', fontSize: '18px' }} />
            <Typography variant="body1">All</Typography> 
          </Box>
        </Box>
      ),
      selector: row => row.id,
      cell: (row) => (
        <input type="checkbox" checked={selectedRows.includes(row.id)} 
        onChange={() => handleRowCheckboxChange(row.id)} />
      ),
      sortable: false,
      style: { 
        padding: '8px',
        textAlign: 'center',
      },
    },
    {
      name: 'Image',
      selector: row => row.image,
      cell: row => (
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <img src={`${row.image}`} alt={row.itemName} style={{ width: 50, height: 50, objectFit: 'cover', marginRight: 8 }} />
        </Box>
      ),
      sortable: false,
      style: { 
        padding: '8px',
      },
    },
    {
      name: 'Name',
      selector: row => row.itemName,
      sortable: true,
      style: { 
        padding: '8px',
      },
    },
    {
      name: 'Items',
      selector: row => row.quantity,
      sortable: true,
      style: { 
        padding: '8px',
      },
    },
    {
      name: 'Publish',
      selector: row => row.publish,
      cell: row => (
        <FormControlLabel
          control={
            <IOSSwitch
              checked={row.publish}
              onChange={() => handlePublishToggle(row, !row.publish)} // Call the function here
            />
          }
          label=""
        />
      ),
      sortable: false,
      style: { 
        padding: '8px',
      },
    },
    {
      name: 'Actions',
      cell: row => (
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Edit
            sx={{
              cursor: 'pointer',
              backgroundColor: 'green',
              color: '#fff',
              borderRadius: '50%',
              padding: '5px',
              fontSize: '28px',
            }}
            onClick={() => navigate(`edit/${row.id}`)}
          />
          <Delete
            sx={{
              cursor: 'pointer',
              backgroundColor: 'red',
              color: '#fff',
              borderRadius: '50%',
              padding: '6px',
              fontSize: '28px',
            }}
            onClick={() => deleteCategory(row.id)}
          />
        </Box>
      ),
      sortable: false,
      style: { 
        padding: '8px',
      },
    },
  ];

  const filteredData = data.filter(item =>
    item.id.toString().includes(searchTerm) ||
    item.itemName.toLowerCase().includes(searchTerm.toLowerCase()) ||
    item.quantity.toString().includes(searchTerm)
  );

  const startIndex = (page - 1) * entriesPerPage;
  const currentPageData = filteredData.slice(startIndex, startIndex + entriesPerPage);

  const handleEntriesChange = (event) => {
    setEntriesPerPage(event.target.value);
    setPage(1);  
  };

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
  };

  return (
    <div>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px',gap: { xs: '10px', md: 0 }, // Apply gap on small screens
    flexWrap: { xs: 'wrap', md: 'nowrap' } }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Typography variant="body1" sx={{ mr: 1 }}>
            Show
          </Typography>
          <FormControl sx={{ p: 1 }}>
            <Select
              value={entriesPerPage}
              onChange={handleEntriesChange}
              displayEmpty
              inputProps={{ 'aria-label': 'Entries per page' }}
            >
              <MenuItem value={10}>10</MenuItem>
              <MenuItem value={25}>25</MenuItem>
              <MenuItem value={50}>50</MenuItem>
              <MenuItem value={100}>100</MenuItem>
            </Select>
          </FormControl>
          <Typography variant="body1" sx={{ mr: 1 }}>
            entries
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Typography variant="body1" sx={{ mr: 1 }}>
            Search:
          </Typography>
          <TextField
            className="recent-or-search-cls"
            id="outlined-basic"
            label=""
            variant="outlined"
            value={searchTerm}
            onChange={handleSearchChange}
            size="small"
          />
        </Box>
      </Box>

      <Box sx={{ border: '1px solid #dee2e6', borderRadius: '8px', overflow: 'auto' }}>
        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px' }}>
            <CircularProgress />
            <Typography variant="body1" sx={{ marginLeft: 2 }}>Categories loading...</Typography>
          </Box>
        ) : (
          <DataTable
            columns={columns}
            data={currentPageData}
            pagination
            paginationServer
            paginationTotalRows={filteredData.length}
            onChangePage={(newPage) => setPage(newPage)}
            paginationPerPage={entriesPerPage}
            paginationComponentOptions={{
              noRowsPerPage: true,
            }}
            customStyles={{
              table: {
                style: {
                },
              },
              cells: {
                style: {
                  padding: '8px',
                  border: '1px solid #dee2e6',
                  color: 'green',
                  fontSize: '14px'
                },
              },
              headCells: {
                style: {
                  backgroundColor: '#f4f4f4',
                  fontWeight: 'bold',
                  padding: '8px',
                  border: '1px solid #dee2e6',
                  fontSize: '1rem'
                },
              },
              rows: {
                style: {
                  '&:hover': {
                    backgroundColor: '#f1f1f1',
                  },
                  '&:nth-child(odd)': {
                    boxShadow: 'inset 0 0 0 9999px rgba(0, 0, 0, 0.023)',
                  },
                },
              },
            }}
          />
        )}
      </Box>
    </div>
  );
};

export default MyTable;
